import { useState } from 'react'
import { object, string, date, array, number } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'
import { type ElementType } from 'react'
import { ActivityButton } from 'components/ActivityButton'
import {
  Box,
  Button,
  Grid,
  InputLabel,
  TextField,
  Stack,
  Select,
  InputAdornment,
  MenuItem,
} from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { isPresent } from '@jjvgaming/player-payback-library'
import type { RewardsCatalog, RewardsCatalogSchedule } from 'types/api'
import { grey } from '@mui/material/colors'
import { useSnackbar } from 'stores/useSnackbar'
import { type ApiError } from 'openapi-typescript-fetch'
import { useQueryClient } from '@tanstack/react-query'
import { useLayout } from 'hooks/useLayout'
import { usePutRewardsCatalog } from 'hooks/api/usePutRewardsCatalog'
import { formatISO, parse, parseISO } from 'date-fns'
import { HookableDateField } from 'components/Shared/HookableDateField'
import { HookableTimeField } from 'components/Shared/HookableTimeField'
import { shouldValidateEndTime, validateEndTime } from 'utils/util'
import { ScheduleRow } from 'pages/ClientManagement/LicensedEstablishments/LicensedEstablishmentNewPage/ScheduleRow'
import { addMissingDays } from '../RewardsCatalogsUtils'

const resolveComponent = (componentSource: unknown): ElementType => {
  if (
    componentSource &&
    typeof componentSource === 'object' &&
    'default' in componentSource
  ) {
    return (componentSource as { default: ElementType }).default
  }

  return componentSource as ElementType
}

const RewardCatalogDetailsEditSchema = object({
  catalogName: string().required('Required').max(100),
  scheduledRewardCatalog: string(),
  startDate: date()
    .nullable()
    .typeError('This date is invalid')
    .transform((currentValue, originalValue, context) => {
      if (context.isType(currentValue)) {
        return currentValue
      }
      if (originalValue === '') {
        return null
      }
      if (originalValue === 'MM/DD/YYYY') {
        return null
      }
      try {
        parse(originalValue, 'MM/DD/YYYY', new Date())
      } catch (error) {
        return new Date('')
      }
      return currentValue
    }),
  startTime: string().when('startDate', {
    is: (startDate: Date | null) => startDate !== null,
    then: () =>
      string().matches(
        /^(0\d|1[0-2]):[0-5]\d [APap][mM]$/,
        'Invalid time format. Please enter a valid time.'
      ),
  }),
  endDate: date()
    .nullable()
    .typeError('This date is invalid')
    .transform((currentValue, originalValue, context) => {
      if (context.isType(currentValue)) {
        return currentValue
      }
      if (originalValue === '') {
        return null
      }
      if (originalValue === 'MM/DD/YYYY') {
        return null
      }
      try {
        parse(originalValue, 'MM/DD/YYYY', new Date())
      } catch (error) {
        return new Date('')
      }
      return currentValue
    })
    .when(
      ['scheduledRewardCatalog', 'startDate'],
      ([scheduledRewardCatalog, startDate], schema) => {
        if (
          scheduledRewardCatalog === 'Yes' &&
          startDate instanceof Date &&
          isFinite(+startDate)
        ) {
          return schema.min(
            startDate,
            'End date should not be before start date'
          )
        }
        return schema
      }
    ),
  endTime: string().when(
    ['startDate', 'startTime', 'endDate'],
    ([startDate, startTime, endDate], schema) => {
      return schema.test({
        name: 'endTime',
        message: 'This field is required',
        test: (value) => {
          const timeRegex = /^(0?[1-9]|1[0-2]):[0-5]\d (AM|PM)$/
          if (
            value &&
            endDate instanceof Date &&
            isFinite(+endDate) &&
            !timeRegex.test(value)
          ) {
            return false
          }

          if (endDate instanceof Date && value?.toString().length === 0) {
            return false
          }

          if (value && endDate instanceof Date && isFinite(+endDate)) {
            shouldValidateEndTime(startDate, startTime, endDate, value) &&
              validateEndTime(startDate, startTime, endDate, value)
          }

          return true
        },
      })
    }
  ),
  schedules: array().of(
    object({
      id: number().nullable().optional(),
      rewardCatalogId: number().optional(),
      dayName: string().optional(),
      opening: string().optional(),
      closing: string().optional(),
    })
  ),
})

export const RewardCatalogDetailsEditModal = ({
  isModalOpen,
  toggleIsOpen,
  toggleModalIsOpen,
  rewardsCatalog,
}: {
  isModalOpen: boolean
  toggleModalIsOpen: () => void
  toggleIsOpen: () => void
  rewardsCatalog: RewardsCatalog
}) => {
  const ActivityButtonComponent = resolveComponent(ActivityButton)
  const FormFieldErrorMessageComponent = resolveComponent(FormFieldErrorMessage)
  const HookableDateFieldComponent = resolveComponent(HookableDateField)
  const HookableTimeFieldComponent = resolveComponent(HookableTimeField)
  const ScheduleRowComponent = resolveComponent(ScheduleRow)
  const EventIconComponent = resolveComponent(EventIcon)
  const AccessTimeIconComponent = resolveComponent(AccessTimeIcon)

  const formMethods = useForm({
    resolver: yupResolver(RewardCatalogDetailsEditSchema),
  })

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = formMethods

  const { isMobile } = useLayout()
  const [selectedScheduledRewardCatalog, setSelectedScheduledRewardCatalog] =
    useState(
      (rewardsCatalog.startDate ??
        (rewardsCatalog.rewardsCatalogSchedules &&
          rewardsCatalog.rewardsCatalogSchedules?.length > 0))
        ? 'Yes'
        : 'No'
    )
  const defaultStartDate = rewardsCatalog.startDate
    ? parseISO(rewardsCatalog.startDate)
    : null

  const defaultEndDate = rewardsCatalog.endDate
    ? parseISO(rewardsCatalog.endDate)
    : null
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const queryClient = useQueryClient()
  const daysToDisplay = addMissingDays(
    rewardsCatalog.rewardsCatalogSchedules ?? []
  )

  const putRewardrewardsCatalogMutation = usePutRewardsCatalog({
    onSuccess: () => {
      setSnackbarMessage(`Reward Catalog has been updated.`)
      toggleModalIsOpen()
      void queryClient.invalidateQueries({
        queryKey: ['/admin/rewards-catalog'],
      })
    },
    onError: (error: ApiError) => {
      if (typeof error.data === 'string') {
        if (error.data.includes('CatalogName')) {
          control.setError('catalogName', { message: error.data })
        }
      } else {
        setSnackbarMessage(
          `Error: Cannot save Reward Catalog information`,
          'error'
        )
      }
    },
  })

  const handleSubmitWrapper = handleSubmit((formData) => {
    let rewardCatalogSchedules: RewardsCatalogSchedule[] = []
    formData.schedules?.forEach((schedule) => {
      if (schedule.opening && schedule.closing) {
        const foundScheduleId =
          rewardsCatalog.rewardsCatalogSchedules?.find(
            (x) => x.dayName === schedule.dayName
          )?.id ?? 0

        rewardCatalogSchedules.push({
          id: foundScheduleId,
          ...schedule,
        })
      }
    })
    // if not scheduling the catalog, clear all schedules and dates
    if (selectedScheduledRewardCatalog === 'No') {
      setValue('startDate', null)
      setValue('startTime', '')
      setValue('endTime', '')
      setValue('endDate', null)
      setValue('schedules', [])
      formData.startDate = null
      formData.endDate = null
      rewardCatalogSchedules = []
    }
    putRewardrewardsCatalogMutation.mutate({
      id: rewardsCatalog.id,
      rewardsCatalogData: {
        name: formData.catalogName,
        description: rewardsCatalog.description,
        type: rewardsCatalog.type,
        startDate: formData.startDate
          ? formatISO(
              parse(formData.startTime ?? '', 'hh:mm a', formData.startDate)
            )
          : null,
        endDate: formData.endDate
          ? formatISO(
              parse(formData.endTime ?? '', 'hh:mm a', formData.endDate)
            )
          : null,
        rewardsCatalogSchedules: rewardCatalogSchedules,
      },
    })
  })

  return (
    <Dialog
      open={isModalOpen}
      maxWidth={'xl'}
      fullWidth
      PaperProps={{ sx: { p: 0, maxWidth: '900px' } }}
      onClose={toggleIsOpen}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmitWrapper} noValidate>
          <DialogTitle bgcolor={grey[50]} sx={{ p: 0 }} className="border-b">
            <Box px={4} py={4}>
              <Typography variant="h3" fontWeight={'bold'}>
                Catalog Details
              </Typography>
              <Typography variant="body-1" color={'text.secondary'}>
                {rewardsCatalog?.name ?? ''}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 4, px: 4 }}>
            <Grid container pt={4}>
              <Grid item xs={12} md={6} pt={{ md: 2 }}>
                <InputLabel
                  required
                  htmlFor="RewardsCatalog.Name"
                  sx={{ fontSize: '16px', fontWeight: '100' }}
                >
                  Reward Catalog Name
                </InputLabel>
                <TextField
                  fullWidth
                  {...register('catalogName', { required: true })}
                  defaultValue={rewardsCatalog?.name ?? ''}
                  error={isPresent(errors.catalogName)}
                />
                {errors.catalogName?.message && (
                  <FormFieldErrorMessageComponent
                    message={errors.catalogName?.message}
                  />
                )}
              </Grid>
              <Stack direction="column" gap={1} width={'450px'} paddingTop={2}>
                <InputLabel
                  htmlFor={'scheduledBoolean'}
                  sx={isMobile ? { fontSize: '16px' } : {}}
                >
                  Do you want to schedule this Reward Catalog?
                </InputLabel>
                <Select
                  {...register('scheduledRewardCatalog', { required: true })}
                  inputProps={{ id: 'scheduledBoolean' }}
                  data-testid="scheduledBoolean"
                  error={isPresent(errors.scheduledRewardCatalog)}
                  defaultValue="No"
                  value={selectedScheduledRewardCatalog}
                  onChange={(e) =>
                    setSelectedScheduledRewardCatalog(e.target.value)
                  }
                >
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                </Select>
                {errors.scheduledRewardCatalog?.message && (
                  <FormFieldErrorMessageComponent
                    message={errors.scheduledRewardCatalog.message}
                  />
                )}
              </Stack>
              {selectedScheduledRewardCatalog === 'Yes' && (
                <div style={{ width: '100%' }}>
                  <Typography variant="body-2">
                    To keep the reward catalog active, don&apos;t select an end
                    date and time.
                  </Typography>
                  <div>
                    <Stack direction={'row'} spacing={2} marginTop={'15px'}>
                      <div style={{ flex: 1, width: '100%' }}>
                        <InputLabel sx={isMobile ? { fontSize: '16px' } : {}}>
                          Start Date
                        </InputLabel>
                        <HookableDateFieldComponent
                          sx={{ width: '100%' }}
                          defaultValue={defaultStartDate}
                          slotProps={{
                            textField: {
                              InputProps: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <EventIconComponent />
                                  </InputAdornment>
                                ),
                              },
                              error: isPresent(errors.startDate),
                            },
                          }}
                          {...register('startDate')}
                        />
                        {errors.startDate?.message && (
                          <FormFieldErrorMessageComponent
                            message={errors.startDate.message}
                          />
                        )}
                      </div>
                      <div style={{ flex: 1, width: '100%' }}>
                        <InputLabel sx={isMobile ? { fontSize: '16px' } : {}}>
                          Start Time
                        </InputLabel>
                        <HookableTimeFieldComponent
                          {...register('startTime')}
                          defaultValue={defaultStartDate as string | null}
                          slotProps={{
                            textField: {
                              InputProps: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <AccessTimeIconComponent />
                                  </InputAdornment>
                                ),
                              },
                              error: isPresent(errors.startTime),
                            },
                          }}
                          sx={{ width: '100%' }}
                        />
                        {errors.startTime?.message && (
                          <FormFieldErrorMessageComponent
                            message={errors.startTime.message}
                          />
                        )}
                      </div>
                    </Stack>
                  </div>
                  <Stack direction={'row'} spacing={2} marginTop={'15px'}>
                    <div style={{ flex: 1, width: '100%' }}>
                      <InputLabel sx={isMobile ? { fontSize: '16px' } : {}}>
                        End Date
                      </InputLabel>
                      <HookableDateFieldComponent
                        sx={{ width: '100%' }}
                        defaultValue={defaultEndDate}
                        slotProps={{
                          textField: {
                            InputProps: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <EventIconComponent />
                                </InputAdornment>
                              ),
                            },
                            error: isPresent(errors.endDate),
                          },
                        }}
                        {...register('endDate')}
                      />
                      {errors.endDate?.message && (
                        <FormFieldErrorMessageComponent
                          message={errors.endDate.message}
                        />
                      )}
                    </div>
                    <div style={{ flex: 1, width: '100%' }}>
                      <InputLabel sx={isMobile ? { fontSize: '16px' } : {}}>
                        End Time
                      </InputLabel>
                      <HookableTimeFieldComponent
                        {...register('endTime')}
                        defaultValue={defaultEndDate as string | null}
                        slotProps={{
                          textField: {
                            InputProps: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <AccessTimeIconComponent />
                                </InputAdornment>
                              ),
                            },
                            error: isPresent(errors.endTime),
                          },
                        }}
                        sx={{ width: '100%' }}
                      />
                      {errors.endTime?.message && (
                        <FormFieldErrorMessageComponent
                          message={errors.endTime.message}
                        />
                      )}
                    </div>
                  </Stack>
                  <Box className="pt-8">
                    <Typography variant="subtitle-1" component={'p'}>
                      Reward Catalog Schedule
                    </Typography>
                    <Typography variant="body-3">
                      {`Select the days you'd like catalog items to be available to patrons`}
                    </Typography>
                    <Stack spacing={2} mt="20px">
                      {daysToDisplay.map((day, index) => {
                        return (
                          <Box
                            key={day.dayName}
                            style={{ height: '85px' }}
                            height={'85'}
                            className="flex flex-col justify-center"
                          >
                            <ScheduleRowComponent
                              day={day.dayName ?? ''}
                              index={index}
                              key={day.dayName}
                              defaultValues={{
                                opening:
                                  rewardsCatalog?.rewardsCatalogSchedules?.find(
                                    (x) => x.dayName === day.dayName
                                  )?.opening ?? '',
                                closing:
                                  rewardsCatalog?.rewardsCatalogSchedules?.find(
                                    (x) => x.dayName === day.dayName
                                  )?.closing ?? '',
                                patronCheckInPointBonusMultiplier: 0,
                              }}
                              openLabel="Enabled"
                              closedLabel="Disabled"
                            />
                          </Box>
                        )
                      })}
                    </Stack>
                  </Box>
                </div>
              )}
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{ borderTop: `1px solid ${grey[300]}`, pt: 2, pb: 2, px: 4 }}
          >
            <Button onClick={toggleModalIsOpen}>Cancel</Button>
            <ActivityButtonComponent
              variant="contained"
              active={false}
              onClick={handleSubmitWrapper}
            >
              Save
            </ActivityButtonComponent>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
